/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { Button, FontAwesomeIcon, HR, Heading, Stack, Text } from "components";
import { useTranslation } from "react-i18next";
import ROUTES from "routes";
import PageLayout from "templates/PageLayout";
import usePreviousRoute from "utils/usePreviousRoute";
import useNotifications from "./useNotifications";
import getFormattedDate from "utils/getFormattedDate";
import LoadingDots from "components/LoadingDots";
import classNames from "classnames";
import useLocalStorage from "utils/useLocalStorage";
import LOCAL_STORAGE_KEYS from "constants/STORAGE_KEYS";
const Notifications = () => {
  const { t } = useTranslation();
  const goBack = usePreviousRoute(ROUTES.dashboard.base);
  const [userId] = useLocalStorage(LOCAL_STORAGE_KEYS.id, null);
  const {
    notifications: notificationsObjectByDate,
    isFetched,
    isError,
    isLoading,
    handleTapToView,
  } = useNotifications(userId);

  return (
    <PageLayout
      title={t("notifications.title")}
      hasGoBack
      hasPolicy={false}
      goBackMethod={goBack}
    >
      {isLoading ? (
        <div className="flex justify-center items-center h-[350px]">
          <LoadingDots />
        </div>
      ) : isError ? (
        <div className="-text--theme-colors-text-body">
          {t("notifications.notificationsError")}
        </div>
      ) : isFetched ? (
        <Stack className="flex mt-5 gap-y-10">
          {Object.keys(notificationsObjectByDate).length > 0 ? (
            Object.keys(notificationsObjectByDate).map((date, i) => {
              return (
                <Stack className="gap-y-7" key={i}>
                  <div>
                    <Heading
                      as="h4"
                      key={date}
                      isBold={false}
                      className="-text--theme-colors-text-body"
                    >
                      {getFormattedDate(date)}
                    </Heading>
                  </div>
                  <Stack className="gap-y-5">
                    {notificationsObjectByDate[date].map((notification) => (
                      <NotificationCard
                        key={notification.id}
                        notification={notification}
                        handleTapToView={handleTapToView}
                      />
                    ))}
                  </Stack>
                </Stack>
              );
            })
          ) : (
            <Text className="text-center -text--theme-colors-text-body">
              {t("notifications.noNotificationsMSG")}
            </Text>
          )}
        </Stack>
      ) : null}
    </PageLayout>
  );
};

export default Notifications;

const NotificationCard = ({ notification, handleTapToView }) => {
  const { t } = useTranslation();
  return (
    <Stack
      className={classNames(
        "-bg--component-colors-gd-notification-card-bg p-7 gap-y-5  -rounded-outer-container",
        { "opacity-80": notification.is_seen }
      )}
    >
      <div className="flex justify-between">
        <div className="flex flex-col gap-y-2.5">
          <Heading
            as="h4"
            className="-text--component-colors-gd-notification-card-medium text-sm"
            isBold={false}
          >
            {notification.title}
          </Heading>
          <Text className="-text--component-colors-gd-notification-card-low">
            {notification.body}
          </Text>
        </div>
      </div>
      {!notification.is_seen && (
        <div className="flex w-full gap-x-12 items-center">
          <HR className="!m-0 !-border--theme-colors-action-primary-default-bg h-0" />
          <Button
            variant={"ghost-unstyled"}
            onClick={() => handleTapToView(notification)}
            className="text-sm !min-h-min h-min"
          >
            <div className="flex gap-x-s10 items-center ">
              <Text className="whitespace-nowrap">
                {t("notifications.notificationViewLabel")}
              </Text>
              <FontAwesomeIcon name="fa-chevron-right" />
            </div>
          </Button>
          <HR className="!m-0 !-border--theme-colors-action-primary-default-bg h-0" />
        </div>
      )}
    </Stack>
  );
};
